import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useSnackbar } from "notistack";
import PublicReportHeader from "../reports/PublicReportHeader";
import { NameDialog, readName, writeName } from "../approvals/ReviewerName";
import { bodyCellSx, cardSx, errorMessage, headCellSx, primaryButtonSx, secondaryButtonSx } from "../approvals/approvalUi";
import { downloadPlanReviewExcel, useBrandDecision, usePlanReview } from "../../hooks/usePlanReview";
import BrandApprovalChip, { BRAND_APPROVAL_META, brandDecisionOf } from "./BrandApprovalChip";
import { formatCount, formatUsername } from "./influencerFormat";
import { getPlatform } from "./platforms";

// /plan-review/:token — the no-login page behind a plan's "Share with brand"
// link. The brand sees the plan's creators (brand-safe fields only — the
// server whitelists them, see pluginbackendnode routes/planReview.js) and
// approves or rejects them, one at a time or in bulk. Decisions can be changed
// or undone any time; the team sees them live in the Plans table.

const STATUS_TABS = ["all", "pending", "approved", "rejected"];
const formatINR = (value) => (value == null ? "—" : `₹${Number(value).toLocaleString("en-IN")}`);
const formatPercent = (value) => (value == null ? "—" : `${Number(value).toFixed(2)}%`);
const text = (value) => (Array.isArray(value) ? value.join(", ") : value) || "—";
const metric = (field) => (inf) => formatCount(inf[field]);
const NOTES_SX = { whiteSpace: "normal", minWidth: 200, maxWidth: 280, fontSize: 13, color: "#374151" };

// How each column the team can share is shown here (keys = BRAND_SHAREABLE_COLUMNS
// in pluginbackendnode/models/plan.js). Which ones appear is the team's choice —
// the server only sends those (`data.columns`). brandCost/customFields expand
// into one column per entry turned on for the current list.
const COLUMN_DEFS = {
  followers: { header: (list) => getPlatform(list?.platform)?.followersLabel ?? "Followers", render: metric("followers") },
  likesAvg: { header: "Avg likes", render: metric("likesAvg") },
  viewsAvg: { header: "Avg views", render: metric("viewsAvg") },
  commentsAvg: { header: "Avg comments", render: metric("commentsAvg") },
  sharesAvg: { header: "Avg shares", render: metric("sharesAvg") },
  repostsAvg: { header: "Avg reposts", render: metric("repostsAvg") },
  engagementRate: { header: "Engagement", render: (inf) => formatPercent(inf.engagementRate) },
  brandCost: {
    expand: (list) => (list?.brandDeliverables ?? []).map((d) => ({
      key: `brandCost:${d}`,
      header: d,
      render: (inf) => formatINR(inf.brandCosts?.find((c) => c.deliverable === d)?.cost),
    })),
  },
  category: { header: "Category", render: (inf) => text(inf.category) },
  language: { header: "Language", render: (inf) => text(inf.language) },
  location: { header: "Location", render: (inf) => text(inf.location) },
  reason: { header: "Why this creator", render: (inf) => text(inf.reason), sx: NOTES_SX },
  customFields: {
    expand: (list) => (list?.customFields ?? []).map((f) => ({
      key: `customField:${f}`,
      header: f,
      render: (inf) => text(inf.customFields?.find((c) => c.field === f)?.value),
      sx: NOTES_SX,
    })),
  },
};

// -> [{key, header, render, sx}] for the current list, in the server's order.
const buildTableColumns = (columns, list) =>
  (columns || []).flatMap((key) => {
    const def = COLUMN_DEFS[key];
    if (!def) return [];
    if (def.expand) return def.expand(list);
    return [{ key, header: typeof def.header === "function" ? def.header(list) : def.header, render: def.render, sx: def.sx }];
  });

const RejectDialog = ({ target, onCancel, onConfirm, busy }) => {
  const [note, setNote] = useState("");
  const count = target?.ids.length || 0;
  return (
    <Dialog open={Boolean(target)} onClose={onCancel} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Reject {count === 1 ? target?.label : `${count} creators`}?
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 1000 }}
          placeholder="Reason (optional) — helps the team suggest better options"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy}
          onClick={() => onConfirm(note.trim())}
          sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}
        >
          Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CreatorCell = ({ inf }) => {
  const platform = getPlatform(inf.platform);
  const handle = formatUsername(inf.username);
  const content = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <Avatar src={inf.avatar || undefined} sx={{ width: 36, height: 36, fontSize: 14 }}>
        {(inf.name || handle || "?").charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, maxWidth: 180 }}>{inf.name || `@${handle}`}</Typography>
          {inf.verified && <VerifiedIcon sx={{ fontSize: 15, color: "#e0a100" }} />}
          {platform && <platform.Icon sx={{ fontSize: 13, color: platform.color }} />}
        </Box>
        {handle && <Typography noWrap sx={{ fontSize: 12, color: "#6B7280" }}>@{handle}</Typography>}
      </Box>
    </Box>
  );
  return inf.profileUrl ? (
    <Box component="a" href={inf.profileUrl} target="_blank" rel="noopener noreferrer" sx={{ color: "inherit", textDecoration: "none", "&:hover .MuiTypography-root:first-of-type": { textDecoration: "underline" } }}>
      {content}
    </Box>
  ) : content;
};

const PlanReviewPage = () => {
  const { token } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, error } = usePlanReview(token);
  const [reviewerName, setReviewerName] = useState(readName);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const decide = useBrandDecision(token, reviewerName);

  const [listId, setListId] = useState(null);
  const [statusTab, setStatusTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [rejectTarget, setRejectTarget] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const lists = useMemo(() => data?.lists ?? [], [data]);
  const influencers = useMemo(() => data?.influencers ?? [], [data]);

  // Default to the first list, and fall back if the team removed the one being viewed.
  useEffect(() => {
    if (lists.length && !lists.some((l) => l.id === listId)) setListId(lists[0].id);
  }, [lists, listId]);

  useEffect(() => {
    if (data && !reviewerName) setNameDialogOpen(true);
  }, [data, reviewerName]);

  const currentList = lists.find((l) => l.id === listId);
  const inList = useMemo(() => influencers.filter((i) => i.listId === listId), [influencers, listId]);
  const counts = useMemo(
    () => STATUS_TABS.reduce((acc, s) => ({ ...acc, [s]: s === "all" ? inList.length : inList.filter((i) => brandDecisionOf(i.brandApproval) === s).length }), {}),
    [inList]
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^@/, "");
    return inList.filter(
      (i) =>
        (statusTab === "all" || brandDecisionOf(i.brandApproval) === statusTab) &&
        (!q || `${i.name || ""} ${formatUsername(i.username) || ""}`.toLowerCase().includes(q))
    );
  }, [inList, statusTab, search]);

  // Selection only ever covers what's on screen — switching list/tab/search clears it
  // so a bulk action can't silently hit rows the brand can no longer see.
  useEffect(() => setSelected(new Set()), [listId, statusTab, search]);

  const tableColumns = useMemo(() => buildTableColumns(data?.columns, currentList), [data, currentList]);
  const allVisibleSelected = visible.length > 0 && visible.every((i) => selected.has(i.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visible.map((i) => i.id)));

  const submit = (ids, decision, note = "") => {
    if (!reviewerName) {
      setNameDialogOpen(true);
      return Promise.resolve();
    }
    return decide
      .mutateAsync({ influencerIds: ids, decision, note })
      .then(() => {
        setSelected(new Set());
        const what = decision === "pending" ? "Decision undone" : `${ids.length === 1 ? "Creator" : `${ids.length} creators`} ${BRAND_APPROVAL_META[decision].label.toLowerCase()}`;
        enqueueSnackbar(what, { variant: "success" });
      })
      .catch((e) => enqueueSnackbar(errorMessage(e), { variant: "error" }));
  };

  const downloadExcel = () => {
    setDownloading(true);
    downloadPlanReviewExcel(token)
      // A blob response hides the JSON error body, so a failed download gets a plain message.
      .catch(() => enqueueSnackbar("Couldn't download the Excel file. Please try again.", { variant: "error" }))
      .finally(() => setDownloading(false));
  };

  const confirmReject = (note) => submit(rejectTarget.ids, "rejected", note).then(() => setRejectTarget(null));

  const saveName = (name) => {
    writeName(name);
    setReviewerName(name);
    setNameDialogOpen(false);
  };

  const shell = (children) => (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <PublicReportHeader campaignName={data?.plan?.name} badgeLabel="Brand review" />
      <Box sx={{ pt: 10, pb: 4, px: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto" }}>{children}</Box>
    </Box>
  );

  if (isLoading) {
    return shell(
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#FF5200" }} />
      </Box>
    );
  }
  if (error) {
    return shell(
      <Box sx={{ ...cardSx, p: 4, textAlign: "center", maxWidth: 520, mx: "auto" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1 }}>This link isn't available</Typography>
        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>{errorMessage(error, "Please ask the team for a new link.")}</Typography>
      </Box>
    );
  }

  const busy = decide.isPending;
  const header = (
    <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Review creators</Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6B7280" }}>
          {data.plan.brandName ? `${data.plan.brandName} · ` : ""}
          Approve the creators you'd like to work with, or reject the ones that aren't a fit. You can change a decision any time.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {reviewerName && (
          <Button size="small" onClick={() => setNameDialogOpen(true)} sx={{ textTransform: "none", color: "#6B7280" }}>
            Reviewing as {reviewerName} · change
          </Button>
        )}
        {influencers.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlinedIcon />}
            onClick={downloadExcel}
            sx={secondaryButtonSx}
          >
            {downloading ? "Preparing…" : "Download Excel"}
          </Button>
        )}
      </Box>
    </Box>
  );

  if (!influencers.length) {
    return shell(
      <>
        {header}
        <Box sx={{ ...cardSx, p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#6B7280" }}>There are no creators to review yet.</Typography>
        </Box>
        <NameDialog key={String(nameDialogOpen)} open={nameDialogOpen} initial={reviewerName} onSave={saveName} />
      </>
    );
  }

  return shell(
    <>
      {header}
      <Box sx={cardSx}>
        {lists.length > 1 && (
          <Tabs value={listId || false} onChange={(_, v) => setListId(v)} variant="scrollable" sx={{ px: 2, borderBottom: "1px solid #F3F4F6" }}>
            {lists.map((l) => (
              <Tab key={l.id} value={l.id} label={l.name} sx={{ textTransform: "none", fontWeight: 600 }} />
            ))}
          </Tabs>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", px: 2, pt: 1.5 }}>
          <Tabs value={statusTab} onChange={(_, v) => setStatusTab(v)} variant="scrollable" sx={{ minHeight: 40 }}>
            {STATUS_TABS.map((s) => (
              <Tab
                key={s}
                value={s}
                label={`${s === "all" ? "All" : BRAND_APPROVAL_META[s].label} (${counts[s]})`}
                sx={{ textTransform: "none", minHeight: 40 }}
              />
            ))}
          </Tabs>
          <TextField
            size="small"
            placeholder="Search creator"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: { xs: "100%", sm: 240 }, "& .MuiInputBase-root": { borderRadius: "10px" } }}
          />
        </Box>

        {selected.size > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mx: 2, mt: 1.5, px: 2, py: 1, borderRadius: "10px", bgcolor: "rgba(255,82,0,0.06)" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{selected.size} selected</Typography>
            <Button size="small" variant="contained" disabled={busy} startIcon={<CheckIcon />} onClick={() => submit([...selected], "approved")} sx={primaryButtonSx}>
              Approve
            </Button>
            <Button size="small" variant="outlined" color="error" disabled={busy} startIcon={<CloseIcon />} onClick={() => setRejectTarget({ ids: [...selected] })} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
              Reject
            </Button>
            <Button size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: "none", color: "#6B7280" }}>Clear</Button>
          </Box>
        )}

        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={headCellSx}>
                  <Checkbox size="small" checked={allVisibleSelected} indeterminate={selected.size > 0 && !allVisibleSelected} onChange={toggleAll} disabled={!visible.length} />
                </TableCell>
                <TableCell sx={{ ...headCellSx, minWidth: 220 }}>Creator</TableCell>
                {tableColumns.map((col) => (
                  <TableCell key={col.key} sx={headCellSx}>{col.header}</TableCell>
                ))}
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx} align="right">Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4 + tableColumns.length} align="center" sx={{ py: 8, border: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: "text.secondary" }}>No creators match</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visible.map((inf) => {
                  const decision = brandDecisionOf(inf.brandApproval);
                  const label = inf.name || `@${formatUsername(inf.username)}`;
                  return (
                    <TableRow key={inf.id} hover selected={selected.has(inf.id)}>
                      <TableCell padding="checkbox" sx={bodyCellSx}>
                        <Checkbox size="small" checked={selected.has(inf.id)} onChange={() => toggle(inf.id)} />
                      </TableCell>
                      <TableCell sx={bodyCellSx}><CreatorCell inf={inf} /></TableCell>
                      {tableColumns.map((col) => (
                        <TableCell key={col.key} sx={{ ...bodyCellSx, ...col.sx }}>{col.render(inf)}</TableCell>
                      ))}
                      <TableCell sx={bodyCellSx}><BrandApprovalChip approval={inf.brandApproval} /></TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: "nowrap" }} align="right">
                        {decision === "pending" ? (
                          <Box sx={{ display: "inline-flex", gap: 1 }}>
                            <Button size="small" variant="outlined" disabled={busy} startIcon={<CheckIcon />} onClick={() => submit([inf.id], "approved")} sx={{ ...secondaryButtonSx, color: "#065F46" }}>
                              Approve
                            </Button>
                            <Button size="small" variant="outlined" disabled={busy} startIcon={<CloseIcon />} onClick={() => setRejectTarget({ ids: [inf.id], label })} sx={{ ...secondaryButtonSx, color: "#991B1B" }}>
                              Reject
                            </Button>
                          </Box>
                        ) : (
                          <Button size="small" disabled={busy} onClick={() => submit([inf.id], "pending")} sx={{ textTransform: "none", color: "#6B7280" }}>
                            Undo
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <RejectDialog key={rejectTarget ? rejectTarget.ids.join() : "closed"} target={rejectTarget} busy={busy} onCancel={() => setRejectTarget(null)} onConfirm={confirmReject} />
      <NameDialog key={String(nameDialogOpen)} open={nameDialogOpen} initial={reviewerName} onSave={saveName} />
    </>
  );
};

export default PlanReviewPage;
